<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/50" @click="close" />

      <!-- Modal -->
      <div
        class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">Edit Post</h2>
          <button
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            @click="close"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" class="text-lg" />
          </button>
        </div>

        <!-- Form -->
        <div class="flex-1 overflow-y-auto p-6 space-y-4">
          <!-- Title -->
          <input
            v-model="form.title"
            type="text"
            placeholder="Title (optional)"
            class="w-full text-base font-semibold text-gray-900 placeholder-gray-300 border-0 border-b border-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 pb-2"
          />

          <!-- Body -->
          <textarea
            v-model="form.body"
            required
            rows="5"
            :maxlength="5000"
            placeholder="Write your post..."
            class="w-full text-sm text-gray-700 placeholder-gray-400 border-0 focus:outline-none focus:ring-0 p-0 resize-none leading-relaxed"
          />
          <p v-if="form.body.length > 4500" class="text-xs text-gray-400 text-right">
            {{ form.body.length }} / 5000
          </p>

          <!-- Type pills (toggleable — click to add/remove type) -->
          <div>
            <p class="text-xs font-medium text-gray-500 mb-2">Type (optional)</p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="tp in typePills"
                :key="tp.type"
                type="button"
                class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border flex items-center gap-1.5"
                :class="form.type === tp.type ? tp.activeClass : 'border-gray-200 text-gray-600 hover:bg-gray-50'"
                @click="toggleType(tp.type)"
              >
                <font-awesome-icon :icon="tp.icon" class="text-xs" />
                {{ tp.label }}
              </button>
            </div>
          </div>

          <!-- Existing photos -->
          <div v-if="existingAttachments.length > 0 || newPhotos.length > 0">
            <p class="text-xs font-medium text-gray-500 mb-2">Photos</p>
            <div class="flex flex-wrap gap-2">
              <!-- Existing attachments -->
              <div
                v-for="att in existingAttachments"
                :key="att.id"
                class="relative group w-16 h-16 rounded-lg overflow-hidden border border-gray-200"
              >
                <img
                  :src="att.url ? resolveFileUrl(att.url) : ''"
                  class="w-full h-full object-cover"
                  :alt="att.fileName"
                />
                <button
                  type="button"
                  class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  :disabled="removingAttachment === att.id"
                  @click="removeExistingAttachment(att)"
                >
                  <font-awesome-icon
                    v-if="removingAttachment !== att.id"
                    :icon="['fas', 'trash']"
                    class="text-white text-xs"
                  />
                  <div
                    v-else
                    class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
                  />
                </button>
              </div>

              <!-- New photos to upload -->
              <div
                v-for="(photo, idx) in newPhotos"
                :key="'new-' + idx"
                class="relative group w-16 h-16 rounded-lg overflow-hidden border border-green-300 border-dashed"
              >
                <img
                  :src="photo.preview"
                  class="w-full h-full object-cover"
                  alt="New photo"
                />
                <button
                  type="button"
                  class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  @click="newPhotos.splice(idx, 1)"
                >
                  <font-awesome-icon :icon="['fas', 'xmark']" class="text-white text-xs" />
                </button>
              </div>

              <!-- Add photo button -->
              <label
                v-if="totalPhotoCount < 10"
                class="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 flex items-center justify-center cursor-pointer transition-colors"
              >
                <font-awesome-icon :icon="['fas', 'plus']" class="text-gray-400" />
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                  multiple
                  class="hidden"
                  @change="handleNewPhotos"
                />
              </label>
            </div>
          </div>

          <!-- Add photos button (when no photos yet) -->
          <div v-if="existingAttachments.length === 0 && newPhotos.length === 0">
            <label
              class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <font-awesome-icon :icon="['fas', 'image']" class="text-xs" />
              Add photos
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                multiple
                class="hidden"
                @change="handleNewPhotos"
              />
            </label>
          </div>

          <!-- Error -->
          <div
            v-if="error"
            class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
          >
            {{ error }}
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <AppButton variant="secondary" size="md" @click="close">Cancel</AppButton>
          <AppButton
            variant="primary"
            size="md"
            :disabled="!form.body.trim() || saving"
            :loading="saving"
            @click="handleSubmit"
          >
            Save Changes
          </AppButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { connectApi, resolveFileUrl } from '@/api'
import type { SpacePost, PostType, PostAttachment, UpdatePostRequest } from '@/api'
import { AppButton } from '@/design-system/components'

const props = defineProps<{
  modelValue: boolean
  editingPost?: SpacePost | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  updated: []
}>()

const typePills: { type: PostType; label: string; icon: string[]; activeClass: string }[] = [
  { type: 'ANNOUNCEMENT', label: 'Announcement', icon: ['fas', 'bullhorn'], activeClass: 'bg-blue-50 text-blue-700 border-blue-300' },
  { type: 'REMINDER', label: 'Reminder', icon: ['fas', 'bell'], activeClass: 'bg-amber-50 text-amber-700 border-amber-300' },
  { type: 'MOMENT', label: 'Moment', icon: ['fas', 'camera'], activeClass: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
]

const form = ref({ type: 'MESSAGE' as PostType, title: '', body: '' })
const saving = ref(false)
const error = ref<string | null>(null)
const existingAttachments = ref<PostAttachment[]>([])
const newPhotos = ref<{ file: File; preview: string }[]>([])
const removingAttachment = ref<string | null>(null)

const totalPhotoCount = computed(() => existingAttachments.value.length + newPhotos.value.length)

function toggleType(type: PostType) {
  form.value.type = form.value.type === type ? 'MESSAGE' : type
}

function close() {
  emit('update:modelValue', false)
}

function handleNewPhotos(e: Event) {
  const input = e.target as HTMLInputElement
  if (!input.files) return
  const maxToAdd = 10 - totalPhotoCount.value
  for (let i = 0; i < Math.min(input.files.length, maxToAdd); i++) {
    const file = input.files.item(i)
    if (!file) continue
    if (file.size > 10 * 1024 * 1024) {
      error.value = `${file.name} is too large (max 10 MB)`
      continue
    }
    newPhotos.value.push({ file, preview: URL.createObjectURL(file) })
  }
  input.value = ''
}

async function removeExistingAttachment(att: PostAttachment) {
  if (!props.editingPost) return
  removingAttachment.value = att.id
  try {
    await connectApi.deleteAttachment(props.editingPost.id, att.id, props.editingPost.spaceId || undefined)
    existingAttachments.value = existingAttachments.value.filter(a => a.id !== att.id)
  } catch (err) {
    console.error('Failed to remove attachment:', err)
    error.value = 'Failed to remove photo. Please try again.'
  } finally {
    removingAttachment.value = null
  }
}

async function handleSubmit() {
  if (!form.value.body.trim() || saving.value || !props.editingPost) return
  saving.value = true
  error.value = null

  try {
    const data: UpdatePostRequest = {
      type: form.value.type,
      title: form.value.title.trim() || undefined,
      body: form.value.body.trim(),
    }
    if (props.editingPost.spaceId) {
      await connectApi.updatePost(props.editingPost.spaceId, props.editingPost.id, data)
    } else {
      await connectApi.updateSchoolWidePost(props.editingPost.id, data)
    }

    // Upload new photos
    for (const photo of newPhotos.value) {
      await connectApi.uploadAttachment(
        props.editingPost.id,
        photo.file,
        props.editingPost.spaceId || undefined,
      )
    }

    emit('updated')
    close()
  } catch (err) {
    console.error('Failed to update post:', err)
    error.value = 'Failed to save. Please try again.'
  } finally {
    saving.value = false
  }
}

watch(
  () => [props.modelValue, props.editingPost] as const,
  ([isOpen, post]) => {
    if (isOpen && post) {
      form.value = {
        type: post.type,
        title: post.title || '',
        body: post.body,
      }
      existingAttachments.value = [...(post.attachments || [])]
      newPhotos.value = []
      error.value = null
    }
  },
  { immediate: true },
)
</script>
